/**
 * @quotech/ui - ErrorBoundary
 * 
 * Shared React Error Boundary for all Quotech hubs.
 * 
 * WHY: Prevents entire app crashes by catching component errors.
 *   - Displays user-friendly error message with recovery options
 *   - Supports resetKeys for automatic reset on dependency changes
 *   - Provides optional onError callback for external logging
 *   - Collapsible technical details for debugging
 *   - ErrorBoundaryWrapper for functional component usage
 * 
 * USAGE:
 *   // Class-based (supports all features)
 *   <ErrorBoundary fallback={<CustomError />} resetKeys={[someId]}>
 *     <MyComponent />
 *   </ErrorBoundary>
 * 
 *   // Functional wrapper (hooks-friendly)
 *   <ErrorBoundaryWrapper resetOnChange={[someId]}>
 *     <MyComponent />
 *   </ErrorBoundaryWrapper>
 */

import { Component } from 'react'
import type { ReactNode } from 'react'
import { cn } from '../lib/utils'
import {
  AlertTriangle,
  RefreshCw,
  Home,
  ChevronDown,
  ChevronUp,
  Bug,
} from 'lucide-react'

// =============================================================================
// TYPES
// =============================================================================

export interface ErrorBoundaryProps {
  children: ReactNode
  fallback?: ReactNode
  onError?: (error: Error, errorInfo: React.ErrorInfo) => void
  resetKeys?: unknown[]
}

interface ErrorBoundaryState {
  hasError: boolean
  error: Error | null
  errorInfo: React.ErrorInfo | null
  showDetails: boolean
}

// =============================================================================
// ERROR BOUNDARY CLASS COMPONENT
// =============================================================================

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props)
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      showDetails: false,
    }
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    this.setState({ errorInfo })

    // Call optional error handler
    this.props.onError?.(error, errorInfo)

    // Log error for debugging
    console.error('[Quotech ErrorBoundary] Caught error:', error, errorInfo)
  }

  componentDidUpdate(prevProps: ErrorBoundaryProps) {
    // Reset error state if resetKeys change
    if (
      this.state.hasError &&
      this.props.resetKeys &&
      prevProps.resetKeys &&
      !this.areKeysEqual(prevProps.resetKeys, this.props.resetKeys)
    ) {
      this.resetError()
    }
  }

  areKeysEqual(prevKeys: unknown[], nextKeys: unknown[]): boolean {
    if (prevKeys.length !== nextKeys.length) return false
    return prevKeys.every((key, index) => key === nextKeys[index])
  }

  resetError = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
      showDetails: false,
    })
  }

  toggleDetails = () => {
    this.setState(state => ({ showDetails: !state.showDetails }))
  }

  render() {
    const { hasError, error, errorInfo, showDetails } = this.state
    const { children, fallback } = this.props

    if (hasError) {
      // Use custom fallback if provided
      if (fallback) {
        return fallback
      }

      // Default error UI - neutral slate/teal colors for all hubs
      return (
        <div className="flex flex-col items-center justify-center min-h-[400px] p-8 text-center">
          {/* Error illustration */}
          <div className="relative mb-6">
            <div className="w-20 h-20 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
              <AlertTriangle className="w-10 h-10 text-red-500" />
            </div>
            <div className="absolute -bottom-1 -right-1 w-8 h-8 rounded-full bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center">
              <Bug className="w-4 h-4 text-amber-500" />
            </div>
          </div>

          {/* Error message */}
          <h2 className="text-xl font-semibold text-slate-900 dark:text-white mb-2">
            Something went wrong
          </h2>
          <p className="text-slate-500 dark:text-slate-400 max-w-md mb-6">
            {error?.message || 'An unexpected error occurred. Please try again or refresh the page.'}
          </p>

          {/* Actions */}
          <div className="flex items-center gap-3 mb-6">
            <button
              onClick={this.resetError}
              className={cn(
                'flex items-center gap-2 px-4 py-2 rounded-lg font-medium',
                'bg-teal-600 text-white hover:bg-teal-500',
                'transition-colors'
              )}
            >
              <RefreshCw className="w-4 h-4" />
              Try again
            </button>
            <button
              onClick={() => window.location.href = '/'}
              className={cn(
                'flex items-center gap-2 px-4 py-2 rounded-lg font-medium',
                'text-slate-600 dark:text-slate-400',
                'hover:bg-slate-100 dark:hover:bg-slate-800',
                'transition-colors'
              )}
            >
              <Home className="w-4 h-4" />
              Go home
            </button>
          </div>

          {/* Error details (collapsible) */}
          {(error || errorInfo) && (
            <div className="w-full max-w-lg">
              <button
                onClick={this.toggleDetails}
                className="flex items-center gap-2 text-sm text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 transition-colors mx-auto mb-2"
              >
                {showDetails ? (
                  <>
                    <ChevronUp className="w-4 h-4" />
                    Hide technical details
                  </>
                ) : (
                  <>
                    <ChevronDown className="w-4 h-4" />
                    Show technical details
                  </>
                )}
              </button>

              {showDetails && (
                <div className="text-left p-4 bg-slate-100 dark:bg-slate-800 rounded-lg overflow-auto max-h-48">
                  <p className="text-xs font-mono text-red-600 dark:text-red-400 mb-2">
                    {error?.name}: {error?.message}
                  </p>
                  {error?.stack && (
                    <pre className="text-xs font-mono text-slate-600 dark:text-slate-400 whitespace-pre-wrap">
                      {error.stack}
                    </pre>
                  )}
                  {errorInfo?.componentStack && (
                    <>
                      <hr className="my-2 border-slate-300 dark:border-slate-600" />
                      <pre className="text-xs font-mono text-slate-500 dark:text-slate-500 whitespace-pre-wrap">
                        Component Stack:{errorInfo.componentStack}
                      </pre>
                    </>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      )
    }

    return children
  }
}

// =============================================================================
// FUNCTIONAL WRAPPER WITH HOOKS SUPPORT
// =============================================================================

interface ErrorBoundaryWrapperProps extends Omit<ErrorBoundaryProps, 'resetKeys'> {
  /** Dependencies that trigger error reset when changed */
  resetOnChange?: unknown[]
}

export function ErrorBoundaryWrapper({
  resetOnChange,
  ...props
}: ErrorBoundaryWrapperProps) {
  return <ErrorBoundary {...props} resetKeys={resetOnChange} />
}

export default ErrorBoundary
